import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useOperationStream } from "@/streaming/useOperationStream";
import { type FlowRunStreamState, flowRunStreamReducer, initialFlowRunStream } from "./flowRunStreamReducer";
import type { FlowRunLocationState } from "./RunFlowDialog";
import { useRefreshRunViews } from "./useRefreshRunViews";

/**
 * Follow a run that was just started from this tab (RunFlowDialog passes its operation in the location state).
 * Returns the live state while the operation is going, else null (the page shows the fetched run).
 *
 * A paused run's operation stays open until it's resumed, and resuming doesn't stream, so the page stops
 * listening once the run pauses or ends and refetches the run instead. It also drops the operation from the history
 * entry, or a reload or Back would replay the old events over the saved run.
 */
export function useLiveFlowRun(flowUuid: string, runUuid: string): FlowRunStreamState | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [opId, setOpId] = useState(() => (location.state as FlowRunLocationState | null)?.opId ?? null);

  const refreshRunViews = useRefreshRunViews();
  const refresh = useCallback(() => void refreshRunViews(flowUuid, runUuid), [refreshRunViews, flowUuid, runUuid]);

  const { pathname, search } = location;
  const stopFollowing = useCallback(() => {
    setOpId(null);
    navigate({ pathname, search }, { replace: true, state: null });
    refresh();
  }, [navigate, pathname, search, refresh]);

  const live = useOperationStream(opId, flowRunStreamReducer, initialFlowRunStream, { onSettled: stopFollowing });

  const paused = live.state.status === "paused";
  useEffect(() => {
    if (paused && opId) stopFollowing();
  }, [paused, opId, stopFollowing]);

  return opId && live.status === "streaming" ? live.state : null;
}
